import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const LoginPage = () => {
  const navigate = useNavigate();
  const { signIn, signUp } = useAuth();

  const [mode, setMode] = useState('signin'); // 'signin' | 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);
    setLoading(true);
    try {
      if (mode === 'signin') {
        await signIn(email, password);
      } else {
        await signUp(email, password, fullName);
      }
      navigate('/leagues');
    } catch (err) {
      setError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-3" style={{ backgroundColor: 'var(--color-primary)' }}>
            <Icon name="Dribbble" size={28} color="#FFFFFF" strokeWidth={2.5} />
          </div>
          <span className="font-heading font-bold text-xl" style={{ color: 'var(--color-foreground)' }}>
            Hoop<span style={{ color: 'var(--color-primary)' }}>Stats</span>
          </span>
        </div>

        {/* Card */}
        <div className="rounded-2xl border p-6" style={{ backgroundColor: 'var(--color-card)', borderColor: 'var(--color-border)', boxShadow: 'var(--shadow-lg)' }}>
          {/* Tabs */}
          <div className="flex rounded-lg overflow-hidden mb-6" style={{ backgroundColor: 'var(--color-muted)' }}>
            <button
              className={`flex-1 py-2.5 font-caption text-sm font-semibold transition-all ${mode === 'signin' ? 'rounded-lg' : ''}`}
              style={mode === 'signin' ? { backgroundColor: 'var(--color-card)', color: 'var(--color-foreground)', boxShadow: 'var(--shadow-sm)' } : { color: 'var(--color-muted-foreground)' }}
              onClick={() => { setMode('signin'); setError(null); }}
            >
              Sign In
            </button>
            <button
              className={`flex-1 py-2.5 font-caption text-sm font-semibold transition-all ${mode === 'signup' ? 'rounded-lg' : ''}`}
              style={mode === 'signup' ? { backgroundColor: 'var(--color-card)', color: 'var(--color-foreground)', boxShadow: 'var(--shadow-sm)' } : { color: 'var(--color-muted-foreground)' }}
              onClick={() => { setMode('signup'); setError(null); }}
            >
              Create Account
            </button>
          </div>

          {/* Title */}
          <h1 className="font-heading font-bold text-xl mb-1" style={{ color: 'var(--color-foreground)' }}>
            {mode === 'signin' ? 'Sign In' : 'Create Account'}
          </h1>
          <p className="font-caption text-sm mb-5" style={{ color: 'var(--color-muted-foreground)' }}>
            {mode === 'signin' ? "Access your team's stats" : 'Set up your HoopStats account'}
          </p>

          {error && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg mb-4 bg-red-50 dark:bg-red-950/20 border border-red-200">
              <Icon name="AlertCircle" size={14} color="#DC2626" />
              <span className="font-caption text-xs text-red-600">{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'signup' && (
              <div>
                <label className="block font-caption text-sm font-medium mb-1.5" style={{ color: 'var(--color-foreground)' }}>Name</label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 -translate-y-1/2">
                    <Icon name="User" size={16} color="var(--color-muted-foreground)" />
                  </div>
                  <input
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e?.target?.value)}
                    placeholder="Your name"
                    autoComplete="name"
                    autoCapitalize="words"
                    className="w-full h-10 pl-9 pr-3 rounded-lg border font-body text-sm"
                    style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block font-caption text-sm font-medium mb-1.5" style={{ color: 'var(--color-foreground)' }}>Email</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <Icon name="Mail" size={16} color="var(--color-muted-foreground)" />
                </div>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  inputMode="email"
                  autoCapitalize="none"
                  className="w-full h-10 pl-9 pr-3 rounded-lg border font-body text-sm"
                  style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}
                />
              </div>
            </div>

            <div>
              <label className="block font-caption text-sm font-medium mb-1.5" style={{ color: 'var(--color-foreground)' }}>Password</label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 -translate-y-1/2">
                  <Icon name="Lock" size={16} color="var(--color-muted-foreground)" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e?.target?.value)}
                  placeholder={mode === 'signup' ? 'Create a password' : 'Your password'}
                  required
                  minLength={6}
                  autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                  className="w-full h-10 pl-9 pr-12 rounded-lg border font-body text-sm"
                  style={{ backgroundColor: 'var(--color-background)', borderColor: 'var(--color-border)', color: 'var(--color-foreground)' }}
                />
                <button
                  type="button"
                  className="absolute right-1 top-1/2 -translate-y-1/2 p-2"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={16} color="var(--color-muted-foreground)" />
                </button>
              </div>
            </div>

            <Button variant="default" fullWidth loading={loading} type="submit" iconName={mode === 'signin' ? 'LogIn' : 'UserPlus'} iconPosition="left">
              {mode === 'signin' ? 'Sign In' : 'Create Account'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
